import { apiService } from './api'
import { PNID_SCALE_LONG_EDGE_PX, rasterizeDrawing } from '@/lib/pdfRasterize'

/**
 * Client-side mirrors of the endpoint's limits.
 *
 * All three are enforced server-side too — these exist so a violation is caught
 * BEFORE the document is created, rather than after. A registration that fails
 * post-upload leaves a catalog row that can never be opened and has no recovery
 * path: the modal's retry re-uploads (creating a second document) and the parse
 * retry route is annotator-blocked.
 */
export const MAX_SLD_PAGES = 12
export const MAX_SLD_PAGE_BYTES = 40 * 1024 * 1024

/**
 * Client-side mirror of the server's `MAX_TOTAL_PAGES_BYTES`.
 *
 * All pages go up in ONE multipart request, so a large set can exceed the
 * body limit. Sized for 300 DPI pages, which are roughly 4x the pixels of the
 * 144 DPI this used to render at; a P&ID-scale render over it falls back to
 * 300 DPI. Checking here fails before spending the upload, and names the
 * page count — the server-side check stays as the authority, this is only so
 * the annotator learns about it in a second rather than after a long POST.
 */
export const MAX_TOTAL_PAGES_BYTES = 120 * 1024 * 1024

export interface SldRasterizeOptions {
  /**
   * Render small sheets up to P&ID scale, so the editor's fixed-width outlines
   * stay in proportion (`PNID_SCALE_LONG_EDGE_PX`). Off by default: it makes
   * each page several times larger, so it follows `sld_parsing_enabled`.
   */
  pnidScale?: boolean
  signal?: AbortSignal
  onProgress?: (done: number, total: number) => void
}

/**
 * Render an SLD's pages, ready to register.
 *
 * Deliberately SEPARATE from the upload, and deliberately FIRST.
 *
 * An SLD has no parser, so a document row without registered pages can never be
 * opened and has no recovery path — the modal's retry re-uploads, creating a
 * second document, and the parse-retry route is annotator-blocked. Uploading
 * before rendering meant every render failure left one of those orphans behind.
 * TIFF made that deterministic rather than theoretical: no browser can decode it
 * in a canvas, so every TIFF upload produced an unopenable row.
 *
 * Rendering first means a failure happens before anything is created, and the
 * annotator simply sees the file rejected.
 */
export async function rasterizeSldPages(
  file: File,
  options: SldRasterizeOptions = {},
): Promise<Blob[]> {
  let pages = await rasterizeDrawing(file, {
    minLongEdgePx: options.pnidScale ? PNID_SCALE_LONG_EDGE_PX : undefined,
    signal: options.signal,
    onProgress: options.onProgress,
  })
  // P&ID scale only keeps the editor's outlines in proportion. A sheet that is
  // over a size limit at that scale but fits at the ordinary one is uploaded at
  // the ordinary one, rather than refused.
  if (options.pnidScale && overLimits(pages)) {
    pages = await rasterizeDrawing(file, { signal: options.signal, onProgress: options.onProgress })
  }

  if (pages.length === 0) {
    throw new Error(`${file.name}: no pages could be rendered`)
  }

  const mb = (n: number) => Math.round(n / 1024 / 1024)

  // Every endpoint limit is checked here, before anything is created. Missing
  // one does not make the upload succeed — it makes it fail AFTER the document
  // exists, which is the orphan this ordering is meant to prevent.
  if (pages.length > MAX_SLD_PAGES) {
    throw new Error(
      `${file.name}: ${pages.length} pages, over the ${MAX_SLD_PAGES}-page limit for one ` +
        `upload. Split the drawing set into smaller files.`,
    )
  }

  const oversized = pages.findIndex((p) => p.size > MAX_SLD_PAGE_BYTES)
  if (oversized >= 0) {
    throw new Error(
      `${file.name}: page ${oversized + 1} renders to ${mb(pages[oversized]!.size)}MB, over the ` +
        `${mb(MAX_SLD_PAGE_BYTES)}MB per-page limit.`,
    )
  }

  const totalBytes = pages.reduce((sum, p) => sum + p.size, 0)
  if (totalBytes > MAX_TOTAL_PAGES_BYTES) {
    throw new Error(
      `${file.name}: ${pages.length} pages render to ${mb(totalBytes)}MB, over the ` +
        `${mb(MAX_TOTAL_PAGES_BYTES)}MB limit for one upload. Split it into smaller files.`,
    )
  }

  return pages
}

function overLimits(pages: Blob[]): boolean {
  return (
    pages.some((p) => p.size > MAX_SLD_PAGE_BYTES) ||
    pages.reduce((sum, p) => sum + p.size, 0) > MAX_TOTAL_PAGES_BYTES
  )
}

/**
 * Attach already-rendered pages to an uploaded SLD document.
 *
 * Throws on failure rather than swallowing: a document whose pages never
 * registered looks uploaded and cannot be annotated, so the caller must be able
 * to mark the row failed instead of green.
 */
export async function registerSldPages(
  documentId: string,
  pages: Blob[],
  options: { signal?: AbortSignal } = {},
): Promise<number> {
  await apiService.registerSldPages(documentId, pages, { signal: options.signal })
  return pages.length
}
